import { FC, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import { getPublicOgImage } from '../site/get-public-og-image';
import { getPublicSiteOrigin } from '../site/get-public-site-origin';
import { buildBreadcrumbJsonLd } from './build-breadcrumb-json-ld';
import { buildHomepageJsonLd } from './build-homepage-json-ld';
import { resolvePageSeo } from './resolve-page-seo';

const SITE_NAME = 'CapQuest';

export const LocationSeo: FC = () => {
  const { pathname } = useLocation();
  const origin = useMemo(() => getPublicSiteOrigin(), []);
  const ogImage = useMemo(() => getPublicOgImage(), []);
  const seo = useMemo(() => resolvePageSeo(pathname), [pathname]);
  const canonicalPath = pathname === '/' ? '' : pathname;
  const canonicalUrl = `${origin}${canonicalPath}`;
  const homeJsonLd = seo.includeJsonLdHomepage ? buildHomepageJsonLd(origin) : null;
  const breadcrumbJsonLd =
    seo.breadcrumbs && seo.breadcrumbs.length > 0
      ? buildBreadcrumbJsonLd(origin, seo.breadcrumbs)
      : null;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{seo.title}</title>
      <meta content={seo.description} name="description" />
      {seo.robots ? <meta content={seo.robots} name="robots" /> : null}
      <link href={canonicalUrl} rel="canonical" />
      <meta content={SITE_NAME} property="og:site_name" />
      <meta content="en_US" property="og:locale" />
      <meta content={seo.title} property="og:title" />
      <meta content={seo.description} property="og:description" />
      <meta content={canonicalUrl} property="og:url" />
      <meta content="website" property="og:type" />
      {ogImage ? <meta content={ogImage} property="og:image" /> : null}
      <meta content={ogImage ? 'summary_large_image' : 'summary'} name="twitter:card" />
      <meta content={seo.title} name="twitter:title" />
      <meta content={seo.description} name="twitter:description" />
      {ogImage ? <meta content={ogImage} name="twitter:image" /> : null}
      {homeJsonLd ? <script type="application/ld+json">{homeJsonLd}</script> : null}
      {breadcrumbJsonLd ? <script type="application/ld+json">{breadcrumbJsonLd}</script> : null}
    </Helmet>
  );
};

LocationSeo.displayName = 'LocationSeo';
